import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { take, switchMap, tap, of, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { IPeerGroupCompetitors } from '../../../../../../core/models/results.model';
import { changeActiveSideTab, loadSidenavData, setIsLoading, setUniverseSelection, submitSearch } from '../../../../../../state/action';
import { AppState, IUniverseSelection } from '../../../../../../state/app.state';
import { selectSidenavDataByTabIdAndKey, selectUniverseSelectionbyTabId } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { CommonModule } from '@angular/common';
import { NgxPaginationModule } from 'ngx-pagination';
import { RouterLink, RouterOutlet } from '@angular/router';
import { NullableNumberPipe } from '../../../../../../shared/pipes/nullable-number.pipe';
import { MatDialog } from '@angular/material/dialog';
import { UniverseSelectorComponent } from 'src/app/shared/components/universe-selector/universe-selector.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { REGION_CODES } from 'public/country-region-codes';
import * as XLSX from 'xlsx';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MainNavbarComponent } from 'src/app/core/components/main-navbar/main-navbar.component';
import { ICompany } from 'src/app/core/models/company.model';
import { AddToWatchlistPopupComponent } from 'src/app/shared/components/add-to-watchlist-popup/add-to-watchlist-popup.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-company-overview',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSlideToggleModule,
    MainNavbarComponent,
    MatTooltipModule,
    MatOptionModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    NullableNumberPipe
  ], templateUrl: './company-overview.component.html',
  styleUrl: './company-overview.component.css'
})
export class CompanyOverviewComponent implements OnInit, OnChanges {
  @Input() tabId!: string;
  @Input() query: string | null = null;
  @Input() gsubind: string[] | null = null;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() entity_data: ICompany | null = null;
  @Input() side_nav_name: string | null = null;
  universeSelection: IUniverseSelection | undefined = undefined;
  showQueryBox: boolean = false;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private dialog: MatDialog
  ) { }

  // Toggle query box
  toggleQueryBox(): void {
    this.showQueryBox = !this.showQueryBox;
  }

  showResults: boolean = false;
  competitors_list_loading_failed: boolean = false;
  competitors_list: IPeerGroupCompetitors[] = [];
  company_data: IPeerGroupCompetitors | undefined = undefined;
  filteredCompanyList: IPeerGroupCompetitors[] = [];
  paginator_1_page: number = 1;
  showOnlyFavorites: boolean = false;
  searchQuery: string = '';
  private searchSubject = new Subject<string>();
  isSearching: boolean = false;

  // Category system
  selectedCategory: string = 'all_competitors';
  categoryTabs = [
    {
      key: 'all_competitors',
      label: 'All Competitors',
      tooltip: 'All companies in GICS, no filter, innovation score ranking.',
      description: 'All companies in the selected peer group, ranked by innovation score.'
    },
    {
      key: 'innovation_leaders',
      label: 'Innovation Leaders',
      tooltip: 'Innovation Leaders are companies that rank highest among competitors based on their innovation score, a composite score reflecting size, growth and quality, relative to the peer group.',
      description: 'Innovation Leaders are the 50 companies that stand out with a strong innovation activity balanced across portfolio size, recent growth and innovation quality.'
    },
    {
      key: 'growth_leaders',
      label: 'Growth Leaders',
      tooltip: 'Growth Leaders are companies with the highest relative growth, among companies with at least 20 inventions published in the last 12 months.',
      description: 'Growth Leaders are the 50 companies which show the most significant recent acceleration in their innovation activity.'
    },
    {
      key: 'size_leaders',
      label: 'Size Leaders',
      tooltip: 'Size Leaders are companies with the highest number of inventions published in the last 10 years.',
      description: 'Size Leaders are the 50 companies with the largest innovation portfolios among competitors, responsible for the largest share of the innovation in their space.'
    },
    {
      key: 'quality_leaders',
      label: 'Quality Leaders',
      tooltip: 'Top Inventors are companies with the highest innovation quality, among companies with at least 10 inventions in the last 12m.',
      description: 'Quality Leaders are the 50 companies that prioritize quality over quantity, likely to produce the largest individual breakthroughs.'
    },
    {
      key: 'upcoming',
      label: 'Up and Coming',
      tooltip: 'Up and Coming are companies with the highest innovation score among companies with at least a third of their inventions filed in the last year.',
      description: 'Up and Coming are the 50 companies that have the strongest innovation activity among companies that have recently increased their efforts in this technology area.'
    },
  ];
  countriesControl = new FormControl<string[]>([]);
  expandedRegions: Set<string> = new Set();

  developedMarketRegion = REGION_CODES.find(region => region.region === 'Developed Markets');
  developedMarketCountries = this.developedMarketRegion
    ? this.developedMarketRegion.countries.map(c => c.code)
    : [];

  countryFilterOptions = REGION_CODES;

  avg_portfolio_size_gics: number = 0;
  avg_recent_growth: number = 0;
  avg_innovation_quality: number = 0;

  universeSelectionData: { universe: IUniverseSelection | undefined, tabId: string | undefined } = { universe: undefined, tabId: undefined }
  hasCustomPeerGroupBeenOpened: boolean = false;
  isUniverseSelectorOpen: boolean = false;


  ngOnInit(): void {
    // Set up debounced search
    this.searchSubject.pipe(
      debounceTime(150),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.applyFilters();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      // Store current tabId to verify in async operations
      const currentTabId = this.tabId;

      this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(data => {
        // Verify we're still on the same tab
        if (this.tabId !== currentTabId) return;

        this.universeSelectionData = {
          universe: data?.universe,
          tabId: data?.tabId
        };
        // Set default universe selection based on company type if not already set
        if (!data?.universe && this.entity_data) {
          const isPublic = this.isPublicCompany(this.entity_data);
          const defaultUniverse: IUniverseSelection = {
            include_public: isPublic,
            include_private: !isPublic
          };
          this.store.dispatch(setUniverseSelection({ selection: defaultUniverse, tabId: this.tabId }));
        }
      });

      this.resetState();

      // Only load data if we're on executive summary, not custom peer group
      if (this.side_nav_name === 'executive_summary' && this.gsubind && this.gsubind[0]) {
        this.loadExecutiveSummaryData();
      } else if (this.side_nav_name === 'competitive_benchmarking_peergroup') {
        // For custom peer group, check universe and show popup if needed
        this.checkCustomPeerGroupState();
      }
    }

    // Handle side_nav_name changes
    if (changes['side_nav_name']) {
      if (this.side_nav_name === 'competitive_benchmarking_peergroup') {
        // Check universe selection for this tab
        this.checkCustomPeerGroupState();
      } else if (this.side_nav_name === 'executive_summary' && this.gsubind && this.gsubind[0]) {
        // Executive summary: use public or private store key (not custom)
        this.loadExecutiveSummaryData();
      }
    }
  }


  // Helper to check if side_nav_name is competitive_benchmarking_peergroup
  isCompetitiveBenchmarkingPeerGroup(): boolean {
    return this.side_nav_name === 'competitive_benchmarking_peergroup';
  }

  // Helper to check if universe selection is a custom peer group
  isCustomPeerGroupSelection(universe: IUniverseSelection | undefined): boolean {
    if (!universe) return false;
    return (universe.include_public && universe.include_private) ||
      (universe.min_mcap !== undefined && universe.min_mcap !== null) ||
      (universe.max_mcap !== undefined && universe.max_mcap !== null);
  }

  // Get store key based on universe selection
  getStoreKey(universe: IUniverseSelection | undefined): string {
    if (!universe) {
      // Default based on company type
      const isPublic = this.entity_data ? this.isPublicCompany(this.entity_data) : true;
      return isPublic ? 'company_public_competitors' : 'company_private_competitors';
    }

    const onlyPublic = universe.include_public && !universe.include_private && !universe.min_mcap && !universe.max_mcap;
    const onlyPrivate = universe.include_private && !universe.include_public && !universe.min_mcap && !universe.max_mcap;
    const isCustomPeerGroup = this.isCustomPeerGroupSelection(universe);

    if (onlyPublic) {
      return 'company_public_competitors';
    } else if (onlyPrivate) {
      return 'company_private_competitors';
    } else if (isCustomPeerGroup) {
      return 'company_detailed_technology_benchmark';
    } else {
      // Fallback to company type
      const isPublic = this.entity_data ? this.isPublicCompany(this.entity_data) : true;
      return isPublic ? 'company_public_competitors' : 'company_private_competitors';
    }
  }


  // Check custom peer group state and show popup or load data accordingly
  checkCustomPeerGroupState(): void {
    const currentTabId = this.tabId;

    // Always clear displayed data first when checking state
    this.showResults = false;
    this.competitors_list = [];
    this.filteredCompanyList = [];
    this.company_data = undefined;
    this.showDataMissing = false;
    this.competitors_list_loading_failed = false;

    // Check universe selection for this tab
    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(universeData => {
      // Verify we're still on the same tab
      if (this.tabId !== currentTabId) return;

      const universe = universeData?.universe;

      // Check if it's public only or private only (not custom)
      const onlyPublic = universe?.include_public && !universe?.include_private && !universe?.min_mcap && !universe?.max_mcap;
      const onlyPrivate = universe?.include_private && !universe?.include_public && !universe?.min_mcap && !universe?.max_mcap;

      if (onlyPublic || onlyPrivate || !universe) {
        // Not a custom peer group - show popup (always show, don't check hasCustomPeerGroupBeenOpened)
        this.hasCustomPeerGroupBeenOpened = true;
        setTimeout(() => {
          this.openUniverseSelectorOnFirstClick();
        }, 100);
      } else {
        // Custom peer group - load data
        this.getCompetitors();
      }
    });
  }

  resetState(): void {
    // Only reset UI state, don't clear data - data should persist in store
    this.showResults = false;
    this.competitors_list = [];
    this.filteredCompanyList = [];
    this.company_data = undefined;
    this.avg_portfolio_size_gics = 0;
    this.avg_recent_growth = 0;
    this.avg_innovation_quality = 0;
    this.competitors_list_loading_failed = false;
    this.selectedCategory = 'all_competitors';
    this.hasCustomPeerGroupBeenOpened = false;
  }

  // Load executive summary data - from public, private, or custom based on universe
  loadExecutiveSummaryData(): void {
    const currentTabId = this.tabId;

    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(universeData => {
      if (this.tabId !== currentTabId) return;

      const universe = universeData?.universe;

      // Determine store key based on universe selection
      const storeKey = this.getStoreKey(universe);

      // Check store first
      this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, storeKey)).pipe(take(1)).subscribe(storedData => {
        if (this.tabId !== currentTabId) return;

        if (storedData && storedData.length > 0) {
          // Data exists in store - use it
          this.processResultData(storedData);
          this.processCompetitorData(storedData);
          this.showResults = true;
          this.showDataMissing = false;
          this.competitors_list_loading_failed = false;
        } else {
          // No data in store - fetch it
          this.getCompetitors();
        }
      });
    });
  }

  showDataMissing: boolean = false;
  showSpinner: boolean = false;

  getCompetitors(forceReload: boolean = false) {
    // Store current tabId to verify in async operations
    const currentTabId = this.tabId;

    this.store.dispatch(setIsLoading({ tabId: this.tabId, isLoading: true }));
    this.showSpinner = true;

    this.company_data = undefined;
    this.showDataMissing = false;
    this.competitors_list = [];
    this.showResults = true;
    this.showOnlyFavorites = false;
    this.competitors_list_loading_failed = false;

    // Get universe selection to determine store key
    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(
      take(1),
      switchMap((universeData) => {
        // Verify we're still on the same tab
        if (this.tabId !== currentTabId) {
          return of();
        }

        const universe = universeData?.universe;
        const storeKey = this.getStoreKey(universe);

        // Check store for existing data
        return this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, storeKey)).pipe(
          take(1),
          switchMap((data) => {
            // Verify we're still on the same tab
            if (this.tabId !== currentTabId) {
              return of();
            }

            if (!data || forceReload) {
              // No data in store or force reload - fetch from API
              return this.apiService.getCompetitiveBenchmarkingPeerGroup(
                { universeSelection: universe },
                this.tabId,
                this.entityids![0],
                this.gsubind![0]
              ).pipe(
                tap((res) => {
                  // Verify we're still on the same tab
                  if (this.tabId !== currentTabId) {
                    return;
                  }

                  this.showSpinner = false;
                  this.store.dispatch(setIsLoading({ tabId: this.tabId, isLoading: false }));

                  if (res.status === 204 || !res.body?.payload?.results?.length) {
                    this.showDataMissing = true;
                    return;
                  }

                  const payload = res.body.payload;
                  if (payload.tabId !== this.tabId) {
                    this.store.dispatch(loadSidenavData({
                      tabId: payload.tabId,
                      sidenavKey: storeKey,
                      data: payload.results
                    }));
                    return;
                  }

                  if (payload.results) {
                    // Store data in appropriate store key
                    this.store.dispatch(loadSidenavData({
                      tabId: this.tabId,
                      sidenavKey: storeKey,
                      data: payload.results
                    }));
                    this.processResultData(payload.results);
                    this.processCompetitorData(payload.results);
                  } else {
                    this.showDataMissing = true;
                  }
                })
              );
            } else {
              // Data exists in store - use it
              // Verify we're still on the same tab
              if (this.tabId !== currentTabId) {
                return of();
              }

              this.showSpinner = false;
              this.competitors_list_loading_failed = false;
              this.processResultData(data);
              this.processCompetitorData(data);
              this.store.dispatch(setIsLoading({ tabId: this.tabId, isLoading: false }));
              return of();
            }
          })
        );
      })
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.showSpinner = false;
          this.store.dispatch(setIsLoading({ tabId: this.tabId, isLoading: false }));
          this.resetState();
          this.competitors_list_loading_failed = true;
        }
      }
    });
  }

  processResultData(data: IPeerGroupCompetitors[]) {
    this.competitors_list = data;

    if (this.competitors_list.length > 0) {
      this.avg_portfolio_size_gics = this.competitors_list.reduce((acc, company) => acc + company.invention_count_120m, 0) / this.competitors_list.length;

      const growthValidCompanies = this.competitors_list.filter(company => company.invention_count_12m_prior !== 0 && company.invention_count_12m !== 0);
      this.avg_recent_growth = growthValidCompanies.reduce((acc, company) => acc + company.relative_growth_12m, 0) / growthValidCompanies.length;

      const qualityValidCompanies = this.competitors_list.filter(company => company.quality_average_12m !== 0);
      this.avg_innovation_quality = qualityValidCompanies.length > 0
        ? qualityValidCompanies.reduce((acc, company) => acc + company.quality_average_12m, 0) / qualityValidCompanies.length
        : 0;
    }

    const entity_data = this.competitors_list.find(c => this.entityids![0] === c.entityid);
    if (entity_data) {
      this.company_data = entity_data;
      this.competitors_list_loading_failed = false;
    } else {
      this.company_data = undefined;
      this.competitors_list_loading_failed = true;
    }
  }

  onReadMoreClick(event: MouseEvent, option: string): void {
    event.preventDefault();
    this.store.dispatch(changeActiveSideTab({ sideTab: option }));
  }


  NavigateBasedOnUniverseSelection() {
    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(universeData => {
      const universe = universeData?.universe;

      if (!universe) {
        // No universe set, use default based on company type
        const isPublic = this.entity_data ? this.isPublicCompany(this.entity_data) : true;
        this.store.dispatch(changeActiveSideTab({ sideTab: isPublic ? 'company_public_competitors' : 'company_private_competitors' }));
        return;
      }

      // Determine navigation based on universe selection
      const onlyPublic = universe.include_public && !universe.include_private && !universe.min_mcap && !universe.max_mcap;
      const onlyPrivate = universe.include_private && !universe.include_public && !universe.min_mcap && !universe.max_mcap;
      const isCustomPeerGroup = this.isCustomPeerGroupSelection(universe);

      if (onlyPublic) {
        this.store.dispatch(changeActiveSideTab({ sideTab: 'company_public_competitors' }));
      } else if (onlyPrivate) {
        this.store.dispatch(changeActiveSideTab({ sideTab: 'company_private_competitors' }));
      } else if (isCustomPeerGroup) {
        this.store.dispatch(changeActiveSideTab({ sideTab: 'competitive_benchmarking_peergroup' }));
      } else {
        // Fallback: use company type
        const isPublic = this.entity_data ? this.isPublicCompany(this.entity_data) : true;
        this.store.dispatch(changeActiveSideTab({ sideTab: isPublic ? 'company_public_competitors' : 'company_private_competitors' }));
      }
    });
  }

  // Navigate to top competitors based on current peer group selection
  onTopCompetitorsClick(event: MouseEvent): void {
    event.preventDefault();

    this.NavigateBasedOnUniverseSelection();
  }

  openUniverseSelector() {
    // Prevent opening if already open
    if (this.isUniverseSelectorOpen) {
      return;
    }

    this.isUniverseSelectorOpen = true;
    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(currentFilter => {
      const hasTableData = this.competitors_list.length > 0 && !this.showDataMissing;
      //check company type, if its is public or public investment firm, then set the company type to public company, otherwise set it to private company
      const companyType = (this.entity_data?.companytype === 'Public Company' || this.entity_data?.companytype === 'Public Investment Firm') ? 'Public Company' : 'Private Company';
      const dialogRef = this.dialog.open(UniverseSelectorComponent, {
        data: {
          min_mcap: currentFilter?.universe?.min_mcap,
          max_mcap: currentFilter?.universe?.max_mcap,
          include_public: currentFilter?.universe?.include_public,
          include_private: currentFilter?.universe?.include_private,
          search_type: 'company',
          showCloseButton: true,
          company_type: companyType
        },
        disableClose: true,
      });

      dialogRef.afterClosed().subscribe(result => {
        this.isUniverseSelectorOpen = false;
        if (!result) {
          this.NavigateBasedOnUniverseSelection();
          return;
        }

        const newSelection: IUniverseSelection = {
          include_private: result.isPrivate,
          include_public: result.isPublic,
          min_mcap: result.min,
          max_mcap: result.max
        };

        // Check if selection should redirect to public or private competitors
        const onlyPublic = result.isPublic && !result.isPrivate && !result.min && !result.max;
        const onlyPrivate = result.isPrivate && !result.isPublic && !result.min && !result.max;

        if (onlyPublic) {
          // Show message in dialog (universe selector is already closed at this point)
          //Check if the current active side tab is exec summary, if yes, then dont show the redirect dialog, just update the data and stay on exec summary page
          if (this.side_nav_name === 'executive_summary') {
            this.store.dispatch(setUniverseSelection({ selection: newSelection, tabId: this.tabId }));
            this.universeSelectionData.universe = newSelection;
            this.getCompetitors(true);
            return;
          }

          const redirectDialog = this.dialog.open(ConfirmationDialogComponent, {
            width: '450px',
            disableClose: true,
            data: {
              title: 'Peer Group Selection',
              message: 'Looks like you have selected public competitors. Redirecting to Public Competitors tab.',
              confirmText: 'OK',
              cancelText: 'Cancel',
              confirmColor: 'primary'
            }
          });

          redirectDialog.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
              // Update universe selection and redirect - dialog closes automatically
              this.store.dispatch(setUniverseSelection({ selection: newSelection, tabId: this.tabId }));
              this.store.dispatch(changeActiveSideTab({ sideTab: 'company_public_competitors' }));
            } else {
              // User clicked Cancel - reopen universe selector
              setTimeout(() => {
                this.openUniverseSelector();
              }, 100);
            }
          });
          return;
        } else if (onlyPrivate) {
          //Check if the current active side tab is exec summary, if yes, then dont show the redirect dialog, just update the data and stay on exec summary page
          if (this.side_nav_name === 'executive_summary') {
            this.store.dispatch(setUniverseSelection({ selection: newSelection, tabId: this.tabId }));
            this.universeSelectionData.universe = newSelection;
            this.getCompetitors(true);
            return;
          }
          // Show message in dialog (universe selector is already closed at this point)
          const redirectDialog = this.dialog.open(ConfirmationDialogComponent, {
            width: '450px',
            disableClose: true,
            data: {
              title: 'Peer Group Selection',
              message: 'Looks like you have selected private competitors. Redirecting to Private Competitors tab.',
              confirmText: 'OK',
              cancelText: 'Cancel',
              confirmColor: 'primary'
            }
          });

          redirectDialog.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
              // Update universe selection and redirect - dialog closes automatically
              this.store.dispatch(setUniverseSelection({ selection: newSelection, tabId: this.tabId }));
              this.store.dispatch(changeActiveSideTab({ sideTab: 'company_private_competitors' }));
            } else {
              // User clicked Cancel - reopen universe selector
              setTimeout(() => {
                this.openUniverseSelector();
              }, 100);
            }
          });
          return;
        }

        // Custom peer group (both types or has mcap filters) - universe selector already closed
        this.store.dispatch(setUniverseSelection({ selection: newSelection, tabId: this.tabId }));
        // update the universe selection local for active peer group selection update in the executive summary page
        this.universeSelectionData.universe = newSelection;
        this.resetCategory();
        this.getCompetitors(true);
      });
    });
  }

  // Open universe selector on first click of custom peer group
  openUniverseSelectorOnFirstClick() {
    this.openUniverseSelector();
  }

  value = 1;
  getCompetitivenessInfo(value: number | undefined): { color: string, text: string } {
    switch (value) {
      case 5: return { color: '#85A010', text: 'Strong Innovative Competitiveness' };
      case 4: return { color: '#A2C614', text: 'Above Average Innovative Competitiveness' };
      case 3: return { color: '#FFC000', text: 'Average Innovative Competitiveness' };
      case 2: return { color: '#F2850E', text: 'Below Average Innovative Competitiveness' };
      case 1: return { color: '#C00000', text: 'Weak Innovative Competitiveness' };
      default: return { color: '#000000', text: 'Unknown Competitiveness' };
    }
  }

  getExecutiveSummaryFlagColor(bracket: number | undefined) {
    switch (bracket) {
      case 5: return '#85A010'
      case 4: return '#A2C614'
      case 3: return '#808080'
      case 2: return '#F2850E'
      case 1: return '#C00000'
      default: return '#000000'
    }
  }


  formatCap(val: number | undefined) {
    if (val == null) return null;
    if (val >= 1e12) return `${(val / 1e12).toFixed(1)}T`;
    if (val >= 1e9) return `${(val / 1e9).toFixed(1)}B`;
    return `${Math.round(val / 1e6)}M`;
  }

  getUniverseDescription(gics: any) {
    if (!this.universeSelectionData.universe) return '';

    const { include_public, include_private, min_mcap, max_mcap } = this.universeSelectionData.universe;

    const formattedMin = this.formatCap(min_mcap);
    const formattedMax = this.formatCap(max_mcap);

    let mcapRange = '';
    if (formattedMin && formattedMax) {
      mcapRange = ` (market cap between ${formattedMin} and ${formattedMax})`;
    } else if (formattedMin) {
      mcapRange = ` (market cap above ${formattedMin})`;
    } else if (formattedMax) {
      mcapRange = ` (market cap below ${formattedMax})`;
    }

    if (include_public && !include_private) {
      return `among public companies in ${gics}${mcapRange}`;
    } else if (!include_public && include_private) {
      return `among private companies in ${gics}`;
    } else if (include_public && include_private) {
      return `among companies in ${gics} within the selected peer group`;
    } else {
      return `in ${gics}`;
    }
  }

  // Get description of custom peer group for display
  getCustomPeerGroupDescription(): string {
    const universe = this.universeSelectionData.universe;
    if (!universe || !this.isCustomPeerGroupSelection(universe)) return '';

    const { include_public, include_private, min_mcap, max_mcap } = universe;
    const parts: string[] = [];

    if (include_public && include_private) {
      parts.push('Public companies');
      if (min_mcap !== undefined && min_mcap !== null) {
        parts.push(`with market cap above ${this.formatCap(min_mcap)}`);
      }
      if (max_mcap !== undefined && max_mcap !== null) {
        parts.push(`with market cap below ${this.formatCap(max_mcap)}`);
      }

      parts.push('and Private companies');
    } else if (include_public) {
      parts.push('Public companies');
      if (min_mcap !== undefined && min_mcap !== null) {
        parts.push(`with market cap above ${this.formatCap(min_mcap)}`);
      }
      if (max_mcap !== undefined && max_mcap !== null) {
        parts.push(`with market cap below ${this.formatCap(max_mcap)}`);
      }
    } else if (include_private) {
      parts.push('Private companies');
    }

    return parts.join(', ');
  }

  // Get active peer group selection description for executive summary
  getActivePeerGroupDescription(): string {
    const universe = this.universeSelectionData.universe;

    // If custom peer group is selected, show custom peer group description
    if (universe && this.isCustomPeerGroupSelection(universe)) {
      return this.getCustomPeerGroupDescription();
    }

    // Otherwise, show based on universe or company type
    if (universe) {
      if (universe.include_public && !universe.include_private) {
        return 'Public companies';
      } else if (universe.include_private && !universe.include_public) {
        return 'Private companies';
      }
    }

    // Fallback to company type
    if (this.entity_data) {
      const isPublic = this.isPublicCompany(this.entity_data);
      return isPublic ? 'Public companies' : 'Private companies';
    }

    return 'Public companies'; // Default fallback
  }

  processCompetitorData(data: IPeerGroupCompetitors[]): void {
    this.competitors_list = data.map((company) => ({
      ...company,
      favorite: company.favorite ?? false,
    }));

    this.filteredCompanyList = [...this.competitors_list];
    this.applyFilters();
  }

  toggleFavorite(company: IPeerGroupCompetitors): void {
    const updatedCompany = { ...company, favorite: !company.favorite };
    const index = this.competitors_list.indexOf(company);
    if (index !== -1) {
      this.competitors_list[index] = updatedCompany;
    }
    this.applyFilters();
  }

  toggleExpand(region: string): void {
    this.expandedRegions.has(region)
      ? this.expandedRegions.delete(region)
      : this.expandedRegions.add(region);
  }

  isExpanded(region: string): boolean {
    return this.expandedRegions.has(region);
  }

  toggleRegionSelection(region: any): void {
    const allSelected = this.isRegionFullySelected(region);
    const regionCodes = region.countries.map((c: any) => c.code);

    const current = this.countriesControl.value ?? [];
    const updated = allSelected
      ? current.filter(code => !regionCodes.includes(code))
      : Array.from(new Set([...current, ...regionCodes]));

    this.countriesControl.setValue(updated);
    this.applyFilters();
  }

  toggleCountrySelection(code: string, region: any): void {
    const current = this.countriesControl.value ?? [];
    const updated = current.includes(code)
      ? current.filter(val => val !== code)
      : [...current, code];

    this.countriesControl.setValue(updated);
    this.applyFilters();
  }

  isRegionFullySelected(region: any): boolean {
    const regionCodes = region.countries.map((c: any) => c.code);
    const selected = this.countriesControl.value ?? [];
    return regionCodes.every((code: string) => selected.includes(code));
  }

  filterFavorites(): void {
    this.showOnlyFavorites = !this.showOnlyFavorites;
    this.applyFilters();
  }


  rankInnovationScore(companies: IPeerGroupCompetitors[]) {
    companies.sort((a, b) => b.innovation_score - a.innovation_score);
    companies.forEach((c, i) => c.rank = i + 1);
  }

  applyFilters(): void {
    this.filteredCompanyList = this.competitors_list;

    // Apply category filter first
    this.filteredCompanyList = this.applyCategoryFilter(this.filteredCompanyList);

    // Apply category ranking BEFORE filtering by selection
    // This ensures ranks are set and won't be recalculated when showing selection only
    this.applyCategoryRanking(this.filteredCompanyList);

    if (this.countriesControl.value?.length) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        this.countriesControl.value!.includes(company.country)
      );
    }

    if (this.showOnlyFavorites) {
      // Show selected companies (starred/selected via star icon)
      // Ranks are already set above, so they won't be recalculated
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        this.selectedCompanies.has(company.entityid) || company.favorite
      );
      this.paginator_1_page = 1;
      // Skip sorting when showing selection only to preserve ranks
      return;
    }

    if (this.searchQuery) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        company.entity_name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }

  }

  // Apply category-specific ranking
  private applyCategoryRanking(companies: IPeerGroupCompetitors[]): void {
    switch (this.selectedCategory) {
      case 'innovation_leaders':
        companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'growth_leaders':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'size_leaders':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'quality_leaders':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'upcoming':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'all_competitors':
      default:
        companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
        companies.forEach((c, i) => c.rank = i + 1);
        break;
    }
  }

  // Apply category filter based on selected category
  applyCategoryFilter(companies: IPeerGroupCompetitors[]): IPeerGroupCompetitors[] {
    if (!companies || companies.length === 0) return [];

    switch (this.selectedCategory) {
      case 'innovation_leaders':
        return companies
          .filter(company => (company.innovation_score) > 0)
          .sort((a, b) => (b.innovation_score) - (a.innovation_score))
          .slice(0, 50);

      case 'growth_leaders':
        return companies
          .filter(company => ((company.invention_count_12m >= 10) && (company.relative_growth_12m > 0)))
          .sort((a, b) => (b.relative_growth_12m) - (a.relative_growth_12m))
          .slice(0, 50);

      case 'size_leaders':
        return companies
          .filter(company => (company.invention_count_120m) > 0)
          .sort((a, b) => (b.invention_count_120m) - (a.invention_count_120m))
          .slice(0, 50);

      case 'quality_leaders':
        return companies
          .filter(company => ((company.invention_count_12m >= 10) && (company.quality_average_12m > 69)))
          .sort((a, b) => (b.quality_average_12m) - (a.quality_average_12m))
          .slice(0, 50);

      case 'upcoming':
        return companies
          .filter(company => {
            const inv1y = company.invention_count_12m;
            const inv10y = company.invention_count_120m;
            const growth = company.relative_growth_12m;
            return inv1y >= 10 && growth > 0 && (inv10y > 0 ? (inv1y / inv10y) >= 0.33 : false);
          })
          .sort((a, b) => (b.innovation_score) - (a.innovation_score))
          .slice(0, 50);

      case 'all_competitors':
      default:
        return companies.sort((a, b) => (b.innovation_score) - (a.innovation_score));
    }
  }

  getIndustryLevel(level: number | string | undefined) {
    switch (level) {
      case '1':
      case 1:
        return 'Sector';
      case '2':
      case 2:
        return 'Sub-Sector';
      case '3':
      case 3:
        return 'Industry';
      case '4':
      case 4:
        return 'Sub-Industry';
      default:
        return 'Unknown';
    }
  }

  newSearch(company: IPeerGroupCompetitors) {
    if (!company.gsubind_extended) {
      company.gsubind_extended = company.gsubind![0];
    }
    this.store.dispatch(submitSearch({
      searchParams: { companies: [company] },
      searchType: 'company'
    }));
  }

  getBarColor(score: number): string {
    if (score >= 90) return 'green';
    if (score >= 50) return 'orange';
    return 'red';
  }

  downloadExcel(): void {
    const list = this.competitors_list.map(item => ({
      Rank: item.rank,
      company: item.entity_name,
      isin: item.isin,
      country: item.country,
      company_type: item.companytype,
      size_10y: item.invention_count_120m ? Math.round(item.invention_count_120m) : item.invention_count_120m,
      absolute_growth: item.invention_count_12m ? Math.round(item.invention_count_12m) : item.invention_count_12m,
      relative_growth: item.relative_growth_12m ? Math.round(item.relative_growth_12m * 10) / 10 : item.relative_growth_12m,
      quality_score: item.quality_average_12m ? Math.round(item.quality_average_12m * 10) / 10 : item.quality_average_12m,
      innovation_score: item.innovation_score ? Math.round(item.innovation_score * 10) / 10 : item.innovation_score,
      inventions_all_time: item.n_inventions ? Math.round(item.n_inventions) : item.n_inventions,
    }));

    const ws = XLSX.utils.json_to_sheet(list);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'competitors');

    ws['!cols'] = [
      { wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }
    ];
    const tabName = this.tab_name ? this.tab_name.substring(0, 30) : 'competitors';
    const fileName = this.tab_name ? `${tabName}_competitors.xlsx` : 'excel_file.xlsx';
    XLSX.writeFile(wb, fileName);
  }

  getOrdinalSuffix(rank: number): string {
    if (rank % 10 === 1 && rank % 100 !== 11) return 'st';
    if (rank % 10 === 2 && rank % 100 !== 12) return 'nd';
    if (rank % 10 === 3 && rank % 100 !== 13) return 'rd';
    return 'th';
  }

  onSearchInputChange(event: any): void {
    const value = event.target.value;

    if (!value || value.trim() === '') {
      this.searchQuery = '';
      this.isSearching = false;
      this.applyFilters();
      return;
    }

    this.isSearching = true;
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.isSearching = false;
    this.applyFilters();
  }

  selectCategory(categoryKey: string): void {
    this.selectedCategory = categoryKey;
    this.paginator_1_page = 1;
    if (categoryKey !== 'all_competitors') {
      this.sortColumn = 'innovation_score';
      this.sortDirection = 'desc';
    }
    this.applyFilters();
  }
  resetCategory(): void {
    this.selectedCategory = 'all_competitors';
    this.paginator_1_page = 1;
  }

  getCurrentCategoryLabel(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.label : 'All Competitors';
  }

  getCurrentCategoryDescription(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.description : '';
  }

  getCurrentCategoryTooltip(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.tooltip : '';
  }

  hasCategoryResults(): boolean {
    return this.filteredCompanyList.length > 0;
  }

  getCategoryResultCount(): number {
    const hasCountryFilter = this.countriesControl.value && this.countriesControl.value.length > 0;
    return hasCountryFilter ? this.filteredCompanyList.length : this.getBaseCategoryCount();
  }

  getCategoryDescription(company: IPeerGroupCompetitors): string {
    const companyName = company.entity_name || 'Unknown Company';
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    const subindustryName = company.gics_custom_name || company.gsubind_extended_name || company.subindustryname;
    const industryLevel = this.getIndustryLevel(company.gics_custom_level);
    if (!category) return '';

    const isCompanyInCategory = this.filteredCompanyList.some(c => c.entityid === company.entityid);
    const hasCountryFilter = this.countriesControl.value && this.countriesControl.value.length > 0;
    const displayCount = hasCountryFilter ? this.filteredCompanyList.length : this.getBaseCategoryCount();
    const countryFilterText = hasCountryFilter ? ' and selected countries' : '';

    switch (this.selectedCategory) {
      case 'innovation_leaders':
        if (isCompanyInCategory) {
          return `<b>${companyName}</b> ranks <b>${company.rank}${this.getOrdinalSuffix(company.rank)}</b> among the <b>${displayCount}</b> <b>Innovation Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName} (GICS Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Innovation Leaders</b> in the selected peer group andthe GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'growth_leaders':
        if (isCompanyInCategory) {
          const growthRank = this.getGrowthRank(company);
          return `<b>${companyName}</b> ranks <b>${growthRank}${this.getOrdinalSuffix(growthRank)}</b> among the <b>${displayCount}</b> <b>Growth Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Growth Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'size_leaders':
        if (isCompanyInCategory) {
          const sizeRank = this.getSizeRank(company);
          return `<b>${companyName}</b> ranks <b>${sizeRank}${this.getOrdinalSuffix(sizeRank)}</b> among the <b>${displayCount}</b> <b>Size Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Size Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}`;
        }

      case 'upcoming':
        if (isCompanyInCategory) {
          const upcomingrank = this.getUpcomingRank(company);
          return `<b>${companyName}</b> ranks <b>${upcomingrank}${this.getOrdinalSuffix(upcomingrank)}</b> among the <b>${displayCount}</b> <b>Up and Coming</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Up and Coming</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'quality_leaders':
        if (isCompanyInCategory) {
          const qualityRank = this.getQualityRank(company);
          return `<b>${companyName}</b> ranks <b>${qualityRank}${this.getOrdinalSuffix(qualityRank)}</b> among the <b>${displayCount}</b> <b>Quality Leaders</b>  competitors in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Quality Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'all_competitors':
      default:
        if (isCompanyInCategory) {
          return `<b>${companyName}</b> ranks <b>${company.rank}${this.getOrdinalSuffix(company.rank)}</b> in innovation strength among <b>${displayCount}</b> competitors in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not present in the current peer group selection and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }
    }
  }

  private getBaseCategoryCount(): number {
    const baseFiltered = this.applyCategoryFilter([...this.competitors_list]);
    return baseFiltered.length;
  }

  private getGrowthRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }

  private getSizeRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }


  private getQualityRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }

  getUpcomingRank(company: IPeerGroupCompetitors): number {
    const baseFiltered = this.applyCategoryFilter([...this.competitors_list]);
    const upcomingCompanies = baseFiltered.filter(c => {
      const inv1y = c.invention_count_12m;
      const inv10y = c.invention_count_120m;
      const growth = c.relative_growth_12m;
      return inv1y >= 10 && growth > 0 && (inv10y > 0 ? (inv1y / inv10y) >= 0.33 : false);
    });

    const sortedCompanies = upcomingCompanies.sort((a, b) => (b.innovation_score) - (a.innovation_score));
    const companyIndex = sortedCompanies.findIndex(c => c.entityid === company.entityid);
    return companyIndex >= 0 ? companyIndex + 1 : 0;
  }



  // Watchlist methods
  isPublicCompany(company: ICompany | IPeerGroupCompetitors | null | undefined): boolean {
    if (!company) return false;
    return (company as any).companytype === 'Public Company' || (company as any).company_type === 'Public Investment Firm';
  }


  // checks if a company is eligible for the wathclist- currently only 1 rule - public company with a valid GICS. More rules can be added here if needed.
  isEligibleForWatchlist(company: ICompany | IPeerGroupCompetitors | null | undefined): boolean {
    if (!company) return false;
    return this.isPublicCompany(company) && !!(company as any).gics_custom_name;
  }

  // opens the add to watchlist popup from the company overview page
  openAddToWatchlist(): void {
    if (!this.company_data || !this.isEligibleForWatchlist(this.company_data)) return;

    const dialogRef = this.dialog.open(AddToWatchlistPopupComponent, {
      width: '600px',
      data: {
        companies: [{
          entityid: this.company_data.entityid!,
          entity_name: this.company_data.entity_name!,
          entity_gics: this.company_data.gsubind_extended || (Array.isArray(this.company_data.gsubind) ? this.company_data.gsubind[0] : this.company_data.gsubind),
          isin: this.company_data.isin,
          companytype: this.company_data.companytype,
          gsubind: this.company_data.gsubind,
          gsubind_extended: this.company_data.gsubind_extended
        }],
        tabId: this.tabId
      }
    });
  }

  // Selection methods for watchlist
  selectedCompanies: Set<string> = new Set();

  toggleCompanySelection(company: IPeerGroupCompetitors): void {
    if (this.selectedCompanies.has(company.entityid!)) {
      this.selectedCompanies.delete(company.entityid!);
    } else {
      this.selectedCompanies.add(company.entityid!);
    }
  }

  isCompanySelected(entityid: string): boolean {
    return this.selectedCompanies.has(entityid);
  }

  toggleSelectAll(): void {
    const eligibleCompanies = this.filteredCompanyList.filter(c => this.isEligibleForWatchlist(c));
    const allSelected = eligibleCompanies.length > 0 && eligibleCompanies.every(c => this.selectedCompanies.has(c.entityid!));
    if (allSelected) {
      eligibleCompanies.forEach(c => this.selectedCompanies.delete(c.entityid!));
    } else {
      eligibleCompanies.forEach(c => this.selectedCompanies.add(c.entityid!));
    }
  }

  getSelectedEligibleCompanies(): IPeerGroupCompetitors[] {
    return this.filteredCompanyList.filter(c =>
      this.selectedCompanies.has(c.entityid!) &&
      this.isEligibleForWatchlist(c)
    );
  }

  // opens the add to watchlist popup from the table view
  openAddToWatchlistFromTable(): void {
    const selected = this.filteredCompanyList.filter(c => this.selectedCompanies.has(c.entityid!));
    const eligible = selected.filter(c => this.isEligibleForWatchlist(c));
    const privateCompanies = selected.filter(c => !this.isEligibleForWatchlist(c));

    if (privateCompanies.length > 0) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '450px',
        data: {
          title: 'Add to Watchlist',
          message: `Out of the ${selected.length} selected companies, only Public Companies (${eligible.length}) will be added to the watchlist. Private Companies (${privateCompanies.length}) cannot be added.`,
          confirmText: 'Continue',
          cancelText: 'Cancel',
          confirmColor: 'primary'
        }
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed && eligible.length > 0) {
          const watchlistDialog = this.dialog.open(AddToWatchlistPopupComponent, {
            width: '600px',
            data: {
              companies: eligible,
              tabId: this.tabId
            }
          });
        }
      });
    } else if (eligible.length > 0) {
      const dialogRef = this.dialog.open(AddToWatchlistPopupComponent, {
        width: '600px',
        data: {
          companies: eligible,
          tabId: this.tabId
        }
      });
    }
  }

  // Sorting methods
  sortColumn: string | null = 'innovation_score';
  sortDirection: 'asc' | 'desc' = 'asc';

  sort(column: string): void {
    if (this.selectedCategory !== 'all_competitors') return;

    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applySorting();
  }
  getSortIcon(column: string): string {
    if (this.selectedCategory !== 'all_competitors') return 'bi-arrow-down-up';
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  applySorting(): void {
    if (!this.sortColumn || this.selectedCategory !== 'all_competitors') return;

    this.filteredCompanyList.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (this.sortColumn) {
        case 'rank':
          aVal = a.rank ?? 0;
          bVal = b.rank ?? 0;
          break;
        case 'size':
          aVal = a.invention_count_120m ?? 0;
          bVal = b.invention_count_120m ?? 0;
          break;
        case 'growth':
          aVal = a.relative_growth_12m ?? 0;
          bVal = b.relative_growth_12m ?? 0;
          break;
        case 'quality':
          aVal = a.quality_average_12m ?? 0;
          bVal = b.quality_average_12m ?? 0;
          break;
        case 'innovation_score':
          aVal = a.innovation_score ?? 0;
          bVal = b.innovation_score ?? 0;
          break;
        case 'score_change':
          aVal = a.innovation_score_change ?? 0;
          bVal = b.innovation_score_change ?? 0;
          break;
        case 'rank_change':
          aVal = a.pct_rank_climbed ?? 0;
          bVal = b.pct_rank_climbed ?? 0;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        return this.sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        return this.sortDirection === 'asc'
          ? aVal - bVal
          : bVal - aVal;
      }
    });
  }



  // Bracket helper methods for rank change styling (matching public competitors)
  getBracketColor(bracket: number | undefined): string {
    if (!bracket) return 'gray';
    const colors = ['dark-red', 'dark-red', 'gray', 'dark-green', 'dark-green'];
    return colors[bracket - 1] || 'gray';
  }

  getBracketArrow(bracket: number | undefined): string {
    if (!bracket) return '';
    if (bracket === 1) return 'bi-chevron-double-down';
    if (bracket === 2) return 'bi-chevron-down';
    if (bracket === 4) return 'bi-chevron-up';
    if (bracket === 5) return 'bi-chevron-double-up';
    return '';
  }

  getExecutiveSummaryFlagImages(bracket: number | undefined): string {
    if (!bracket) return '';
    if (bracket === 1) return 'assets/red_green_flags/Red2.svg';
    if (bracket === 2) return 'assets/red_green_flags/Red1.svg';
    if (bracket === 3) return 'assets/red_green_flags/NoFlag.svg';
    if (bracket === 4) return 'assets/red_green_flags/Green1.svg';
    if (bracket === 5) return 'assets/red_green_flags/Green2.svg';
    return '';
  }



  getBracketTooltip(bracket: number | undefined) {
    if (!bracket) return '';
    if (bracket === 1) return 'Among the bottom 5% globally.';
    if (bracket === 2) return 'Among the bottom 10% globally.';
    if (bracket === 3) return null;
    if (bracket === 4) return 'Among the top 10% globally.';
    if (bracket === 5) return 'Among the top 5% globally.';
    return null;
  }

  // Monitoring methods for executive summary
  hasPublicCompetitorsData(): boolean {
    return this.company_data !== undefined && this.isPublicCompany(this.company_data);
  }

  getScoreChange(): number | null {
    if (!this.company_data) return null;
    const data = this.company_data as any;
    return data.innovation_score_change !== null && data.innovation_score_change !== undefined ? data.innovation_score_change : null;
  }

  getRankChange(): number | null {
    if (!this.company_data) return null;
    const data = this.company_data as any;
    return data.pct_rank_climbed !== null && data.pct_rank_climbed !== undefined ? data.pct_rank_climbed : null;
  }


  getScoreBracket(): number {
    const change = this.getScoreChange();
    if (change === null) return 0;
    if (change > 2) return 5;
    if (change > 1) return 4;
    if (change > 0) return 3;
    if (change > -1) return 2;
    return 1;
  }

  getRankBracket(): number {
    const change = this.getRankChange();
    if (change === null) return 0;
    if (change > 5) return 5;
    if (change > 2) return 4;
    if (change > 0) return 3;
    if (change > -2) return 2;
    return 1;
  }


  getRankClimbed(): number {
    const change = this.getRankChange();
    return change !== null ? Math.abs(change) : 0;
  }
}
